import { useCallback, type ComponentType } from 'react';
import { Grid } from 'react-flexbox-grid';
import StepWizard from 'components/common/StepWizard';
import useFormValuesRef from 'hooks/useFormValuesRef';
import requireValue from 'utils/requireValue';
import {
  AgeOfHeatingSystem,
  AmountOfHotWater,
  EnergyStandard,
  HeatingSystemTypes,
  HotWaterProducers,
  HouseTypes,
  PowerConsumers,
  Renovations,
  RoomTemperature,
  SolarHeatingSystemTypes,
} from 'graphql/generated/graphql';
import RenovationHouseCommonInfo from './tabs/RenovationHouseThirdStep';
import EnergyRenovations from './tabs/RenovationHouseFourthStep';
import IndoorRenovations from './tabs/RenovationHouseFifthStep';
import OutdoorRenovations from './tabs/RenovationHouseSixthStep';
import OtherInvestments from './tabs/RenovationHouseSeventhStep';
import CO2CalculatorForm from './tabs/co2-calculator-form/CO2CalculatorFormFirstStep';
import Nav from '../Nav';
import s from '../calculators.module.scss';
import CO2Status from './tabs/CO2StatusSecondStep';
import CostOverview from '../CostOverviewTab';
import type {
  IRenovationHouseFormValues,
  IRenovationHouseStepProps,
  TRenovationHouseSubmit,
} from './types';

const formValueMock: Partial<IRenovationHouseFormValues> = {
  name: 'My house',
  country: 'AT',
  zip: '1414',

  heated_living_area: 220,
  kind_of_house: HouseTypes.single_family_house,
  energy_standard: EnergyStandard.between_1960_and_1975,
  renovations: [
    Renovations.new_windows,
    Renovations.insulation_top_ceiling,
    Renovations.insulation_basement_ceiling,
    Renovations.insulation_facade,
  ],
  is_solar_heating_system: true,
  solar_heating_system_type: SolarHeatingSystemTypes.heating_and_hot_water,
  number_of_people: 4,
  heating_system: HeatingSystemTypes.natural_gas,
  age_of_heating_system: AgeOfHeatingSystem.between_10_and_20_years,
  temperature: RoomTemperature.less_than_21,
  hot_water_producer: HotWaterProducers.electric_boiler,
  amount_of_hot_water: AmountOfHotWater.medium,
  is_devices_younger_than_10_years: true,
  power_consumers: [PowerConsumers.sauna, PowerConsumers.outdoor_whirlpool],
  is_solar_power_system: true,
  solar_power_system_size: 10,
  solar_power_system_consuming_percentage: 15,
};

interface IRenovationHouseTab {
  label: string;
  component: ComponentType<IRenovationHouseStepProps>;
}

interface IRenovationHouseFormProps {
  initialValues: IRenovationHouseFormValues;
  onSubmit: TRenovationHouseSubmit;
  isEditMode?: boolean;
  /** The saved wizard step, null when the wizard was completed (edit mode). */
  step?: number | null;
}

const tabs: IRenovationHouseTab[] = [
  { label: 'project_details.tabs.house_info', component: CO2CalculatorForm },
  { label: 'project_details.tabs.co2_status', component: CO2Status },
  { label: 'project_details.tabs.common', component: RenovationHouseCommonInfo },
  { label: 'project_details.tabs.energy', component: EnergyRenovations },
  { label: 'project_details.tabs.indoor', component: IndoorRenovations },
  { label: 'project_details.tabs.outdoor', component: OutdoorRenovations },
  { label: 'project_details.tabs.other', component: OtherInvestments },
  { label: 'project_details.tabs.cost_overview', component: CostOverview },
];

const RenovationHouseForm = ({
  initialValues,
  onSubmit,
  isEditMode,
  step,
}: IRenovationHouseFormProps) => {
  const formValuesRef = useFormValuesRef([], { formValueMock, initialValues });

  const onSubmitStep = useCallback(
    (values: IRenovationHouseFormValues) => {
      formValuesRef.current = { ...formValuesRef.current, ...values };
    },
    [formValuesRef],
  );

  const onBack = useCallback(
    (values: IRenovationHouseFormValues, callback: (() => void) | undefined) => {
      onSubmitStep(values);
      // StepWizard passes `previousStep` to every step.
      requireValue(callback, 'call')();
    },
    [onSubmitStep],
  );

  return (
    <Grid className={s.gridContainer}>
      <StepWizard
        // StepWizard starts at the first step for a null step as for a missing one.
        initialStep={step ?? undefined}
        nav={<Nav isEditMode={isEditMode} tabs={tabs} />}
      >
        {tabs.map(({ label, component: C }) => (
          <C
            key={label}
            {...{ onSubmitStep, onBack, formValuesRef, initialValues, onSubmit, isEditMode }}
          />
        ))}
      </StepWizard>
    </Grid>
  );
};

export default RenovationHouseForm;
